import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import BasicIcon from '../BasicIcon/BasicIcon';
import Input from '../Input/Input';
import { HelperContainer, HelperMessage } from '../Input/styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import {
  DragContainer,
  DragErrorMessage,
  DragText,
  DragTextBaseName,
  DragTextExtension,
  FolderContainer,
  HiddenInput,
  ImageSourceSelector,
  ImageSourceSelectorContainer,
  ImageSourceSelectorItem,
} from './styles';
import { Base64ObjectFileType, FileInputProps } from './types';

const FileInput: React.FC<FileInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    fileTypes,
    helperText,
    showClearIcon,
    multipleFiles = false,
    base64 = false,
    multiFileText = 'Archivos',
    dragable = false,
    dragAndDropText = multipleFiles ? 'Arrastrá o subí los archivos' : 'Arrastrá o subí el archivo',
    truncateFileName = false,
    canUploadPicture = false,
    openFromGalleryText = 'Elegí de la galeria',
    takePictureText = 'Sacar Foto',
    uploadFileText = 'Subir archivo',
    handleCapturePicture,
    handleOpenFromGallery,
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<string>('');
  const [hadUserInput, setHadUserInput] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const [openImgSrcSelector, setOpenImgSrcSelector] = useState(false);
  const imageSourceSelectorRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const inputRef = useRef<HTMLInputElement>(null);
  const splitFileName = useCallback((fileName: string) => {
    const trimmedFileName = fileName.trim();
    if (!trimmedFileName) return { baseName: '', extension: '' };

    const lastDotIndex = trimmedFileName.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === trimmedFileName.length - 1) {
      return { baseName: trimmedFileName, extension: '' };
    }

    return {
      baseName: trimmedFileName.slice(0, lastDotIndex),
      extension: trimmedFileName.slice(lastDotIndex),
    };
  }, []);

  const currentValue = valueRef.current;
  const dragDisplayText = currentValue || `${dragAndDropText} ${fileTypes.join(' ')}`;
  const { baseName: fileBaseName, extension: fileExtension } = splitFileName(currentValue || '');

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
      valueRef.current = '';
      inputFileRef.current.value = '';
    }
    if (onChange) {
      onChange(undefined);
    }
  }, [input, onChange]);

  useEffect(() => {
    if (hadUserInput) return;
    if (!input.value) {
      valueRef.current = '';
      return;
    }
    if (base64) {
      valueRef.current = Array.isArray(input.value)
        ? `${input.value.length} ${multiFileText}`
        : input.value.name;
    } else {
      const files: FileList = input.value;
      const filesArray = Array.from(files);
      const inputValue =
        filesArray.length > 1 ? `${filesArray.length} ${multiFileText}` : filesArray[0].name;
      valueRef.current = inputValue;
    }
    input.onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64, multiFileText, input.value]);

  const processFiles = useCallback(
    (files: FileList) => {
      if (!files) return;
      setHadUserInput(true);
      const filesArray = Array.from(files);
      if (filesArray.length === 0) return;
      const inputValue =
        filesArray.length > 1 && multipleFiles
          ? `${filesArray.length} ${multiFileText}`
          : filesArray[0].name;
      valueRef.current = inputValue;
      if (!base64) {
        input.onChange(multipleFiles ? files : files[0]);
        if (onChange) onChange(multipleFiles ? files : files[0]);
        return;
      }
      if (multipleFiles) {
        const filesBase64: Base64ObjectFileType[] = [];
        filesArray.forEach((file) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            filesBase64.push({ name: file.name, data: reader.result });
            if (filesBase64.length === filesArray.length) {
              input.onChange(filesBase64);
              if (onChange) onChange(filesBase64);
            }
          };
        });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
          input.onChange({ name: files[0].name, data: reader.result });
          if (onChange) onChange({ name: files[0].name, data: reader.result });
        };
      }
      if (input?.onBlur) input.onBlur();
    },
    [base64, input, multiFileText, multipleFiles, onChange],
  );

  const onFileChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      processFiles(files);
    },
    [processFiles],
  );

  const handleFileSearch = useCallback(() => {
    if (disabled) return;

    if (canUploadPicture) {
      setOpenImgSrcSelector(true);
      return;
    }
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  }, [canUploadPicture, setOpenImgSrcSelector, disabled]);

  const handleMouseDown = useCallback(() => {
    handleFileSearch();
    if (onMouseDown) onMouseDown();
  }, [onMouseDown, handleFileSearch]);

  const renderFileIcon = useCallback(
    () => (
      <>
        {!valueRef.current && (
          <FolderContainer
            data-testid={`${testId}-folder-icon`}
            disabled={disabled}
            onClick={handleFileSearch}
          >
            <BasicIcon
              size="small"
              name="folder-open"
              color={hasError ? actualTheme.status.error.background : actualTheme.gray4D}
            />
          </FolderContainer>
        )}
      </>
    ),
    [
      testId,
      disabled,
      handleFileSearch,
      hasError,
      actualTheme.status.error.background,
      actualTheme.gray4D,
    ],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled) return;

      const { files }: { files: FileList } = event.dataTransfer;
      processFiles(files);
    },
    [processFiles, disabled],
  );

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled) {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.dropEffect = 'none';
      }
    },
    [disabled],
  );

  const takePicture = useCallback(async () => {
    const fileList = await handleCapturePicture();
    processFiles(fileList);
    setOpenImgSrcSelector(false);
  }, [handleCapturePicture, processFiles]);

  const openFromGallery = useCallback(async () => {
    const fileList = await handleOpenFromGallery();
    processFiles(fileList);
    setOpenImgSrcSelector(false);
  }, [handleOpenFromGallery, processFiles]);

  const goBackMobileHandler = useCallback((event: any) => {
    event.detail.register(10, () => {
      setOpenImgSrcSelector(false);
    });
  }, []);

  const handleImgSrcSelectorClose = useCallback((event) => {
    if (
      !imageSourceSelectorRef.current.contains(event.target) &&
      !inputRef.current.contains(event.target)
    ) {
      setOpenImgSrcSelector(false);
    }
  }, []);

  const handleImgFromFiles = useCallback(() => {
    if (inputFileRef.current) inputFileRef.current.click();
    setOpenImgSrcSelector(false);
  }, []);

  useEffect(() => {
    if (openImgSrcSelector) {
      document.addEventListener('ionBackButton', goBackMobileHandler);
      document.addEventListener('mousedown', handleImgSrcSelectorClose);
    } else {
      document.removeEventListener('ionBackButton', goBackMobileHandler);
      document.removeEventListener('mousedown', handleImgSrcSelectorClose);
    }
  }, [openImgSrcSelector, goBackMobileHandler, handleImgSrcSelectorClose]);

  return (
    <ThemeProvider theme={actualTheme}>
      {dragable ? (
        <>
          <DragContainer
            disabled={disabled}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleFileSearch}
            hasError={hasError}
          >
            <BasicIcon
              disabled={disabled}
              name="folder-add"
              size="tab-size-large"
              color={hasError ? actualTheme.status.error.default : actualTheme.gray4D}
              clickable
            />
            <DragText truncateSingleLine={truncateFileName && !!valueRef.current}>
              {truncateFileName && currentValue && fileExtension ? (
                <>
                  <DragTextBaseName title={currentValue}>{fileBaseName}</DragTextBaseName>
                  <DragTextExtension>{fileExtension}</DragTextExtension>
                </>
              ) : (
                dragDisplayText
              )}
            </DragText>
            <HelperContainer>
              {hasError && (
                <>
                  <DragErrorMessage disabled={disabled} data-testid={`${testId}-error`}>
                    {meta.error}
                  </DragErrorMessage>
                </>
              )}
            </HelperContainer>
          </DragContainer>
          <HelperContainer>
            {helperText && (
              <HelperMessage disabled={disabled} data-testid={`${testId}-help-message`}>
                {helperText}
              </HelperMessage>
            )}
          </HelperContainer>
        </>
      ) : (
        <Input
          testId={testId}
          label={label}
          readOnly={readOnly || disabled}
          disabled={disabled}
          value={valueRef.current}
          input={input}
          meta={meta}
          autoComplete="off"
          size={size}
          variant={variant}
          helperText={helperText}
          showClearIcon={showClearIcon}
          placeholder={placeholder}
          onClear={handleClear}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseDown={handleMouseDown}
          rightAddOn={renderFileIcon()}
          typingAllowed={false}
          pointer
          inputRef={inputRef}
        />
      )}
      <HiddenInput
        ref={inputFileRef}
        type="file"
        accept={fileTypes.join(',')}
        onChange={onFileChange}
        multiple={multipleFiles}
      />

      <ImageSourceSelectorContainer isOpen={openImgSrcSelector} />
      <ImageSourceSelector isOpen={openImgSrcSelector} ref={imageSourceSelectorRef}>
        {handleCapturePicture && (
          <ImageSourceSelectorItem role="presentation" onClick={takePicture}>
            <BasicIcon name="camera" />
            {takePictureText}
          </ImageSourceSelectorItem>
        )}
        {handleOpenFromGallery && (
          <ImageSourceSelectorItem role="presentation" onClick={openFromGallery}>
            <BasicIcon name="image" />
            {openFromGalleryText}
          </ImageSourceSelectorItem>
        )}
        <ImageSourceSelectorItem role="presentation" onClick={handleImgFromFiles}>
          <BasicIcon name="upload" />
          {uploadFileText}
        </ImageSourceSelectorItem>
      </ImageSourceSelector>
    </ThemeProvider>
  );
};

export default FileInput;
